import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing session
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);
      } catch (err) {
        console.error('Error checking session:', err);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user || null);
        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    setError(null);
    try {
      // Nur die spezifische Admin-E-Mail-Adresse zulassen
      if (email === 'news@hopp.sg' && password === 'admin8970') {
        // Manuelles Setzen des Benutzers, da wir keine echte Auth verwenden
        setUser({
          id: '1',
          email: email,
          role: 'admin'
        });
        return { data: true };
      } else {
        setError('Ungültige Anmeldeinformationen');
        return { error: new Error('Ungültige Anmeldeinformationen') };
      }
    } catch (err) {
      setError(err.message);
      return { error: err };
    }
  };

  const register = async (email, password) => {
    setError(null);
    try {
      // Registrierung ist nicht erlaubt
      setError('Registrierung ist nicht erlaubt');
      return { error: new Error('Registrierung ist nicht erlaubt') };
    } catch (err) {
      setError(err.message);
      return { error: err };
    }
  };

  const logout = async () => {
    setError(null);
    try {
      // Benutzer abmelden
      setUser(null);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { error: err };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        setError,
        login,
        register,
        logout,
        isAuthenticated: !!user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};